import {
  ComponentRef,
  computed,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import {
  QaiCallAudioIndicatorComponent,
  QaiCallAudioIndicatorSize,
  QaiCallAudioIndicatorStatus,
  QaiIconComponent,
  QaiIconKind,
} from '@qai/ui';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { isFirefox } from 'src/app/utils/device/device.util';
import { ScreenSharePipCompositor } from './screen-share-pip/screen-share-pip-compositor';
import { ScreenSharePipControls } from './screen-share-pip/screen-share-pip-controls';
import { PIP_VIDEO_CLASS, ScreenSharePipView } from './screen-share-pip/screen-share-pip-view';

interface DocumentPictureInPicture {
  requestWindow(options: {
    width: number;
    height: number;
    disallowReturnToOpener?: boolean;
    preferInitialWindowPlacement?: boolean;
  }): Promise<Window>;
}

const AVATAR_VIDEO_ID = 'avatar-video-realtime';
const USER_VIDEO_ID = 'user-video';
const AVATAR_HIDDEN_CLASS = 'avatar-video-hidden';
const PIP_WINDOW_SIZE = { width: 300, height: 484 };
const REFRESH_INTERVAL_MS = 400;

const AUTO_RESUME_MS = 3000;
const WEBKIT_PIP_TIMEOUT_MS = 1000;
const WEBKIT_PIP_EXIT_COOLDOWN_MS = 1000;
const STANDARD_PIP_RECONCILE_MS = 1000;

type WebkitPresentationMode = 'inline' | 'picture-in-picture';

type WebkitVideo = HTMLVideoElement & {
  webkitSetPresentationMode?: (mode: WebkitPresentationMode) => void;
  webkitPresentationMode?: WebkitPresentationMode;
};

interface MovedVideo {
  video: HTMLVideoElement;
  placeholder: Comment;
  originalMuted: boolean;
  wasHidden: boolean;
  stopAutoResume: () => void;
}

@Injectable({ providedIn: 'root' })
export class ScreenSharePipService {
  private readonly logger = inject(LoggerService);
  private readonly injector = inject(EnvironmentInjector);
  private pipWindow: Window | null = null;
  private view: ScreenSharePipView | null = null;
  private compositor: ScreenSharePipCompositor | null = null;
  private standardPipTarget: HTMLVideoElement | null = null;
  private refreshHandle: ReturnType<typeof setInterval> | undefined;
  private stopWatchingStandardPip: (() => void) | null = null;
  private moved: MovedVideo[] = [];
  private openGeneration = 0;
  private readonly iconCache = new Map<QaiIconKind, string>();
  private indicators: ComponentRef<QaiCallAudioIndicatorComponent>[] = [];
  private readonly _closedByUser = signal(false);
  readonly closedByUser = this._closedByUser.asReadonly();
  private readonly _isStandardPipOpen = signal(false);
  readonly isStandardPipOpen = this._isStandardPipOpen.asReadonly();
  private readonly _isDocumentPipOpen = signal(false);
  readonly isDocumentPipOpen = this._isDocumentPipOpen.asReadonly();
  readonly isAvatarInPip = computed(() => this._isDocumentPipOpen() || this._isStandardPipOpen());
  private readonly _isClosing = signal(false);
  readonly isClosing = this._isClosing.asReadonly();
  private exitCooldown: ReturnType<typeof setTimeout> | undefined;

  get isDocumentPipSupported(): boolean {
    return 'documentPictureInPicture' in window;
  }

  private get isOpen(): boolean {
    return !!this.pipWindow || this.isInStandardPip(this.standardPipTarget);
  }

  private isInStandardPip(video: WebkitVideo | null): boolean {
    if (!video?.isConnected) return false;
    return video.webkitSetPresentationMode
      ? video.webkitPresentationMode === 'picture-in-picture'
      : document.pictureInPictureElement === video;
  }

  findMediaElement<T extends HTMLElement = HTMLElement>(id: string): T | null {
    const inMain = document.getElementById(id);
    if (inMain) return inMain as T;
    return (this.pipWindow?.document.getElementById(id) as T | null) ?? null;
  }

  prepare(): void {
    if (this.isDocumentPipSupported || !document.pictureInPictureEnabled || this.compositor) return;
    const avatar = document.getElementById(AVATAR_VIDEO_ID) as HTMLVideoElement | null;
    if (!avatar?.srcObject) return;
    this.compositor = new ScreenSharePipCompositor(avatar);
    this.compositor.start();
  }

  async open(controls: ScreenSharePipControls): Promise<boolean> {
    if (this.isOpen) return true;

    const avatar = document.getElementById(AVATAR_VIDEO_ID) as HTMLVideoElement | null;
    const hasAvatarStream = !!avatar?.srcObject;
    if (this.isDocumentPipSupported) {
      if (!avatar || !hasAvatarStream) return this.notReady(avatar);
      return this.openDocumentPip(avatar, controls);
    }

    const frame = this.compositor?.isReady ? this.compositor : null;
    const target = frame?.video ?? (hasAvatarStream ? avatar : null);
    if (!target) return this.notReady(avatar);
    return this.openStandardPip(target, frame);
  }

  private notReady(avatar: HTMLVideoElement | null): false {
    this.logger.log('[CALL] screenshare - PiP not opened: avatar video not ready', {
      avatarFound: !!avatar,
      avatarHasStream: !!avatar?.srcObject,
      frameReady: !!this.compositor?.isReady,
    });
    return false;
  }

  close(): void {
    this.openGeneration++;
    this._closedByUser.set(false);

    const pipWindow = this.pipWindow;
    if (pipWindow) {
      pipWindow.removeEventListener('pagehide', this.handlePipWindowClosedByUser);
      if (!pipWindow.closed) {
        this.logger.log('[CALL] screenshare - PiP closed');
        pipWindow.close();
      }
    }
    this.restore();

    const target: WebkitVideo | null = this.standardPipTarget;
    this.standardPipTarget = null;
    this.unwatchStandardPip();
    if (this.isInStandardPip(target)) {
      this.logger.log('[CALL] screenshare - PiP closed');
      if (target?.webkitSetPresentationMode) this.startExitCooldown();
    }
    this.exitStandardPip(target);
    this._isStandardPipOpen.set(false);
    this.compositor?.setActive(false);
  }

  dispose(): void {
    this.close();
    if (!this.compositor) return;
    this.compositor.stop();
    this.compositor = null;
  }

  private async openDocumentPip(
    avatar: HTMLVideoElement,
    controls: ScreenSharePipControls,
  ): Promise<boolean> {
    const generation = this.openGeneration;
    let pipWindow: Window;
    try {
      const { documentPictureInPicture } = window as unknown as {
        documentPictureInPicture: DocumentPictureInPicture;
      };
      pipWindow = await documentPictureInPicture.requestWindow({
        ...PIP_WINDOW_SIZE,
        disallowReturnToOpener: true,
        preferInitialWindowPlacement: true,
      });
    } catch (e) {
      this.logger.log('[CALL] screenshare - PiP not opened: browser refused', {
        error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      });
      return false;
    }

    if (generation !== this.openGeneration) {
      pipWindow.close();
      return false;
    }

    this.pipWindow = pipWindow;
    const view = new ScreenSharePipView(
      pipWindow,
      controls,
      (kind) => this.iconSvg(kind),
      (size, status) => this.audioIndicator(size, status),
      isFirefox(),
    );
    this.view = view;
    this.copyStylesheets(pipWindow);

    const user = document.getElementById(USER_VIDEO_ID) as HTMLVideoElement | null;
    this.moveVideos([
      [avatar, view.avatarSlot],
      ...(user ? [[user, view.userSlot] as [HTMLVideoElement, HTMLElement]] : []),
    ]);
    this.refreshHandle = setInterval(() => this.view?.refresh(), REFRESH_INTERVAL_MS);
    this._isDocumentPipOpen.set(true);

    pipWindow.addEventListener('pagehide', this.handlePipWindowClosedByUser);
    this._closedByUser.set(false);
    this.logger.log(
      `[CALL] screenshare - PiP opened (${isFirefox() ? 'Firefox, reduced toolbar' : 'Chrome/Edge'})`,
    );
    return true;
  }

  private async openStandardPip(
    target: HTMLVideoElement,
    compositor: ScreenSharePipCompositor | null,
  ): Promise<boolean> {
    if (!document.pictureInPictureEnabled) {
      this.logger.log('[CALL] screenshare - PiP not opened: not supported in this browser');
      return false;
    }
    this.unwatchStandardPip();
    this.exitStandardPip(this.standardPipTarget);
    this.standardPipTarget = null;
    const generation = this.openGeneration;
    compositor?.setActive(true);
    this.resumePlayback(target);
    try {
      const video = target as WebkitVideo;
      await (video.webkitSetPresentationMode
        ? this.enterWebkitPip(video)
        : target.requestPictureInPicture());
    } catch (e) {
      compositor?.setActive(false);
      this.logger.log('[CALL] screenshare - PiP not opened: browser refused', {
        error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      });
      return false;
    }
    if (generation !== this.openGeneration) {
      compositor?.setActive(false);
      this.exitStandardPip(target);
      return false;
    }
    this.standardPipTarget = target;
    this.watchStandardPip(target, compositor);
    this._isStandardPipOpen.set(true);
    this._closedByUser.set(false);
    this.logger.log(
      `[CALL] screenshare - PiP opened (Safari${compositor ? '' : ', plain avatar: frame not ready'})`,
    );
    return true;
  }

  private watchStandardPip(
    target: HTMLVideoElement,
    compositor: ScreenSharePipCompositor | null,
  ): void {
    const onClosed = () => {
      this.unwatchStandardPip();
      this.standardPipTarget = null;
      this._isStandardPipOpen.set(false);
      compositor?.setActive(false);
      // Closing with Safari's X pauses the video, which freezes the avatar if it was the one floating.
      this.resumePlayback(target);
      this._closedByUser.set(true);
      this.logger.log('[CALL] screenshare - PiP closed by the user');
    };
    const reconcile = () => {
      if (document.visibilityState === 'visible' && !this.isInStandardPip(target)) onClosed();
    };
    const reconcileHandle = setInterval(reconcile, STANDARD_PIP_RECONCILE_MS);
    target.addEventListener('leavepictureinpicture', onClosed);
    this.stopWatchingStandardPip = () => {
      clearInterval(reconcileHandle);
      target.removeEventListener('leavepictureinpicture', onClosed);
    };
  }

  private unwatchStandardPip(): void {
    this.stopWatchingStandardPip?.();
    this.stopWatchingStandardPip = null;
  }

  private resumePlayback(video: HTMLVideoElement): void {
    if (video.paused) video.play().catch(() => undefined);
  }

  private enterWebkitPip(video: WebkitVideo): Promise<void> {
    return new Promise((resolve, reject) => {
      const settle = () => {
        clearTimeout(timer);
        video.removeEventListener('webkitpresentationmodechanged', settle);
        if (video.webkitPresentationMode === 'picture-in-picture') resolve();
        else reject(new DOMException('Safari did not enter Picture-in-Picture', 'NotAllowedError'));
      };
      const timer = setTimeout(settle, WEBKIT_PIP_TIMEOUT_MS);
      video.addEventListener('webkitpresentationmodechanged', settle);
      video.webkitSetPresentationMode!('picture-in-picture');
    });
  }

  private startExitCooldown(): void {
    clearTimeout(this.exitCooldown);
    this._isClosing.set(true);
    this.exitCooldown = setTimeout(() => this._isClosing.set(false), WEBKIT_PIP_EXIT_COOLDOWN_MS);
  }

  private exitStandardPip(video: WebkitVideo | null): void {
    const logFailure = (e: unknown) =>
      this.logger.log('[CALL] screenshare - PiP exit failed', {
        error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      });
    try {
      if (video?.webkitPresentationMode === 'picture-in-picture') {
        video.webkitSetPresentationMode?.('inline');
      } else if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(logFailure);
      }
    } catch (e) {
      logFailure(e);
    }
  }

  private readonly handlePipWindowClosedByUser = (): void => {
    this.logger.log('[CALL] screenshare - PiP closed by the user');
    this.restore();
    this._closedByUser.set(true);
  };

  private iconSvg(kind: QaiIconKind): string {
    const cached = this.iconCache.get(kind);
    if (cached !== undefined) return cached;
    const ref = createComponent(QaiIconComponent, { environmentInjector: this.injector });
    ref.setInput('kind', kind);
    ref.changeDetectorRef.detectChanges();
    const svg = (ref.location.nativeElement as HTMLElement).innerHTML;
    ref.destroy();
    this.iconCache.set(kind, svg);
    return svg;
  }

  private audioIndicator(
    size: QaiCallAudioIndicatorSize,
    status: QaiCallAudioIndicatorStatus,
  ): HTMLElement {
    const ref = createComponent(QaiCallAudioIndicatorComponent, {
      environmentInjector: this.injector,
    });
    ref.setInput('size', size);
    ref.setInput('status', status);
    ref.changeDetectorRef.detectChanges();
    this.indicators.push(ref);
    return ref.location.nativeElement;
  }

  private copyStylesheets(pipWindow: Window): void {
    const copies = pipWindow.document.createDocumentFragment();
    Array.from(document.styleSheets).forEach((styleSheet) => {
      try {
        const style = pipWindow.document.createElement('style');
        style.textContent = Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join('');
        copies.appendChild(style);
      } catch {
        if (styleSheet.href) {
          const link = pipWindow.document.createElement('link');
          link.rel = 'stylesheet';
          link.href = styleSheet.href;
          copies.appendChild(link);
        }
      }
    });
    pipWindow.document.head.prepend(copies);
  }

  private moveVideos(targets: [HTMLVideoElement, HTMLElement][]): void {
    this.moved = targets.map(([video, slot]) => {
      const placeholder = document.createComment('pip-placeholder');
      video.parentElement?.insertBefore(placeholder, video);

      const originalMuted = video.muted;
      const wasHidden = video.classList.contains(AVATAR_HIDDEN_CLASS);
      video.muted = true;
      video.classList.remove(AVATAR_HIDDEN_CLASS);
      video.classList.add(PIP_VIDEO_CLASS);
      slot.prepend(video);
      const stopAutoResume = this.autoResume(video);
      video.play().catch(() => undefined);

      return { video, placeholder, originalMuted, wasHidden, stopAutoResume };
    });
  }

  private autoResume(video: HTMLVideoElement): () => void {
    let active = true;
    const resume = () => {
      setTimeout(() => {
        if (active && video.paused) video.play().catch(() => undefined);
      }, 100);
    };
    const stop = () => {
      active = false;
      clearTimeout(timer);
      video.removeEventListener('pause', resume);
    };
    const timer = setTimeout(stop, AUTO_RESUME_MS);
    video.addEventListener('pause', resume);
    return stop;
  }

  private restore(): void {
    clearInterval(this.refreshHandle);
    this.refreshHandle = undefined;
    this.view = null;
    this.indicators.forEach((ref) => ref.destroy());
    this.indicators = [];
    this._isDocumentPipOpen.set(false);
    for (const { video, placeholder, originalMuted, wasHidden, stopAutoResume } of this.moved) {
      stopAutoResume();
      video.classList.remove(PIP_VIDEO_CLASS);
      video.classList.toggle(AVATAR_HIDDEN_CLASS, wasHidden);
      video.muted = originalMuted;

      if (placeholder.isConnected && placeholder.parentNode) {
        placeholder.parentNode.insertBefore(video, placeholder);
        if (video.paused) video.play().catch(() => undefined);
      } else {
        try {
          document.adoptNode(video);
        } catch (e) {
          this.logger.error('[CALL] screenshare - failed to adopt video back from PiP', e);
        }
      }
      placeholder.remove();
    }
    this.moved = [];
    this.pipWindow = null;
  }
}
